import React, { useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { AiOutlineDownload, AiOutlineFolderOpen } from "react-icons/ai";
import dayjs from "dayjs"; // for date formatting (optional, can use Date too)
import api from '../services/api';

// === Real API Section ===
import { API_CONFIG } from '../config/env';
const API_BASE_URL = `${API_CONFIG.BASE_URL}/documents`;

async function getDetails() {
    try {
        // Use the API service which handles authentication properly
        const response = await api.request('/documents/user-tax-forms/');
        
        const taxForms = response.results || response.data || [];
        
        // Extract unique years and filter for Tax Organizer forms
        const taxOrganizerForms = taxForms.filter(form => form.category_name === 'Tax Organizer');
        const years = [...new Set(taxOrganizerForms.map(form => form.year_display))].sort((a, b) => b - a);
        
        return {
            years: years,
            entries: taxOrganizerForms,
        };
    } catch (error) {
        console.error('Error fetching tax forms:', error);
        throw new Error('Failed to fetch tax forms. Please make sure you are logged in.');
    }
}

async function getUserDownloadHistory() {
    try {
        // Fetch user-specific download history from API
        const response = await api.request('/documents/downloads/');
        return response.results || response.data || [];
    } catch (error) {
        console.error('Error fetching download history:', error);
        return [];
    }
}

async function downloadForm({ id, year, category, filename }) {
    try {
        // Use the API service which handles authentication properly
        const response = await api.request('/documents/user-tax-forms/download/', {
            method: 'POST',
            body: JSON.stringify({ form_id: id })
        });
        
        // Download directly to device without opening new tab or redirecting
        if (response.download_url) {
            // Fetch the file as a blob to avoid navigation
            const token = localStorage.getItem('authToken');
            const fileResponse = await fetch(response.download_url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (fileResponse.ok) {
                const blob = await fileResponse.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = response.filename || 'tax-form.pdf';
                link.style.display = 'none';
                link.target = '_self'; // Ensure it doesn't open in new tab
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url); // Clean up the object URL
            } else {
                // Fallback: try direct download with proper attributes to prevent navigation
                const link = document.createElement('a');
                link.href = response.download_url;
                link.download = response.filename || 'tax-form.pdf';
                link.target = '_self'; // Use _self instead of _blank
                link.style.display = 'none';
                link.rel = 'noopener noreferrer'; // Security attributes
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
        
        // Return download record (API will handle the actual logging)
        const now = dayjs().format("YYYY-MM-DD HH:mm:ss");
        return { 
            id: response.download_id, 
            year, 
            category, 
            filename: response.filename, 
            datetime: now 
        };
    } catch (error) {
        console.error('Download error:', error);
        throw new Error('Download failed. Please make sure you are logged in.');
    }
}

export default function DownloadTaxInformationForm() {
    // State
    const [years, setYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState(null);
    const [records, setRecords] = useState([]);
    const [downloadHistory, setDownloadHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState('');

    // Table settings
    const [perPage, setPerPage] = useState(10);

    // Check authentication and fetch data on mount
    useEffect(() => {
        async function loadInitial() {
            setLoading(true);
            setAuthError('');
            
            // Clear any cached download history to prevent data leakage
            localStorage.removeItem('downloadHistory');
            
            // Check if user is authenticated
            const token = localStorage.getItem('authToken');
            if (!token) {
                setAuthError('Please log in to access tax forms.');
                setIsAuthenticated(false);
                setLoading(false);
                return;
            }
            
            try {
                const resp = await getDetails();
                setYears(resp.years);
                setSelectedYear(resp.years[0]);
                
                // Show available forms
                const availableForms = resp.entries.filter(e => e.year_display === resp.years[0]);
                setRecords(availableForms);
                
                // Load user-specific download history from API
                const downloadHistory = await getUserDownloadHistory();
                setDownloadHistory(downloadHistory);
                
                setIsAuthenticated(true);
            } catch (error) {
                console.error('Error loading tax forms:', error);
                setAuthError(error.message || 'Failed to load tax forms. Please try logging in again.');
                setIsAuthenticated(false);
            }
            
            setLoading(false);
        }
        loadInitial();
    }, []);

    // When selectedYear changes, fetch records for that year
    useEffect(() => {
        async function fetchByYear() {
            if (!selectedYear) return;
            setLoading(true);
            const resp = await getDetails();
            const availableForms = resp.entries.filter(e => e.year_display === selectedYear);
            setRecords(availableForms);
            setLoading(false);
        }
        fetchByYear();
    }, [selectedYear]);

    // Table columns for react-data-table-component
    const columns = useMemo(
        () => [
            {
                name: "S.No",
                selector: (row, idx) => idx + 1,
                width: "80px",
                sortable: false,
                cell: (row, index) => index + 1,
            },
            {
                name: "Category",
                selector: row => row.form_category,
                sortable: true,
                style: { minWidth: "120px" },
                cell: row => (
                    <span style={{ color: "#217eed", fontWeight: 500 }}>
                        {row.form_category || 'N/A'}
                    </span>
                ),
            },
            {
                name: "Year",
                selector: row => row.form_year,
                sortable: true,
                width: "100px",
                cell: row => (
                    <span style={{ fontWeight: 600, color: "#112" }}>
                        {row.form_year || 'N/A'}
                    </span>
                ),
            },
            {
                name: "File Name",
                selector: row => row.form_title,
                sortable: true,
                style: { minWidth: "200px" },
                cell: row => (
                    <span style={{ color: "#4f46e5", fontWeight: 600 }}>
                        {row.form_title || 'N/A'}
                    </span>
                ),
            },
            {
                name: "Downloaded At",
                selector: row => row.downloaded_at,
                sortable: true,
                style: { minWidth: "180px" },
                cell: row => (
                    <span style={{ color: "#555", fontSize: 14 }}>
                        {row.downloaded_at ? new Date(row.downloaded_at).toLocaleString() : 'N/A'}
                    </span>
                ),
            },
        ],
        []
    );

    // On year button click: download file directly without changing page state
    async function handleYearDownload(year) {
        try {
            // Fetch all forms to find the one for the selected year
            const resp = await getDetails();
            const row = resp.entries.find(r => r.year_display === year);
            
            if (!row) {
                console.log(`No tax form available for year ${year}`);
                return;
            }
            
            // Download the form directly
            await downloadForm(row);
            
            // Refresh download history from API (silently in background)
            const downloadHistory = await getUserDownloadHistory();
            setDownloadHistory(downloadHistory);
            
            // Show brief success feedback (optional)
            console.log(`Tax form for ${year} downloaded successfully`);
            
        } catch (error) {
            console.error('Download failed:', error);
            // You could add a toast notification here if needed
        }
    }


    // Table custom styles for react-data-table-component
    const customStyles = {
        rows: {
            style: {
                fontSize: 15,
                minHeight: "38px",
                color: "#232243",
                backgroundColor: "#f9f9fb",
            },
            highlightOnHoverStyle: {
                backgroundColor: "#e0e7ff",
                color: "#3730a3",
                cursor: "pointer",
            },
        },
        headRow: {
            style: {
                fontWeight: 700,
                fontSize: 15,
                background: "#eceffd",
                color: "#4338ca",
                minHeight: "46px",
            },
        },
        headCells: {
            style: {
                borderBottom: "1.5px solid #cdd2fe",
                paddingLeft: "18px",
                paddingRight: "18px",
            },
        },
        cells: {
            style: {
                paddingLeft: "18px",
                paddingRight: "18px",
            },
        },
        pagination: {
            style: {
                borderTop: "solid 1px #eceffd",
                color: "#4338ca",
                fontWeight: 600,
            },
        },
    };

    return (
        <section>
            <h2
                style={{
                    // fontFamily: "italic",
                    // fontWeight: 500,
                    // color: "#4338ca",
                    // letterSpacing: "0.03em",
                    // fontSize: 32,
                    // marginBottom: 23,

                    fontSize: 38,
                    marginRight: 10,
                    color: "#4338ca",
                    verticalAlign: "middle",
                    filter: "drop-shadow(0px 1px 4px #b5beff77)"
                }}
            >
              <AiOutlineFolderOpen />  Download Form
            </h2>

            {/* Authentication Error Display */}
            {authError && (
                <div
                    style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "16px 20px",
                        marginBottom: 20,
                        color: "#dc2626",
                        fontSize: 16,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        gap: 10
                    }}
                >
                    <span style={{ fontSize: 20 }}>⚠️</span>
                    {authError}
                </div>
            )}

            {/* Show loading or not authenticated message */}
            {loading && (
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#666",
                        fontSize: 16
                    }}
                >
                    Loading tax forms...
                </div>
            )}

            {/* Show message when not authenticated */}
            {!isAuthenticated && !loading && authError && (
                <div
                    style={{
                        textAlign: "center",
                        padding: "40px 20px",
                        color: "#666",
                        fontSize: 16
                    }}
                >
                    Please log in to access tax forms.
                </div>
            )}

            {/* Main Content - Only show when authenticated */}
            {isAuthenticated && !loading && (
                <>
                    {/* Year Chips */}
                    <div
                        style={{
                            display: "flex",
                            gap: 15,
                            marginBottom: 30,
                            flexWrap: "wrap",
                        }}
                    >
                {years.map((year) => (
                    <button
                        key={year}
                        type="button"
                        style={{
                            background:
                                selectedYear === year
                                    ? "linear-gradient(90deg,#4338ca 40%,#6366f1 100%)"
                                    : "#eceffd",
                            color: selectedYear === year ? "#fff" : "#4338ca",
                            border: "none",
                            borderRadius: 22,
                            fontWeight: 700,
                            fontSize: 16,
                            padding: "8px 24px",
                            boxShadow: selectedYear === year ? "0 2px 10px #4338ca22" : "",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 7,
                            outline: selectedYear === year ? "2px solid #a5b4fc" : "none",
                            transition: "background 0.18s",
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleYearDownload(year);
                        }}
                    >
                        <AiOutlineDownload style={{ fontSize: 18 }} />
                        {year}
                    </button>
                ))}
            </div>

            {/* Main Table */}
            <div>
                {downloadHistory.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#666',
                        fontSize: '16px',
                        background: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                    }}>
                        <AiOutlineDownload size={48} style={{ marginBottom: '16px', color: '#ccc' }} />
                        <p style={{ margin: 0, fontWeight: 500 }}>No downloads yet</p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#888' }}>
                            Click on a year button above to download tax forms
                        </p>
                    </div>
                ) : (
                    <DataTable
                        key={`${selectedYear}-${perPage}`}
                        title=""
                        columns={columns}
                        data={downloadHistory}
                        progressPending={loading}
                        highlightOnHover
                        striped
                        customStyles={customStyles}
                        pagination
                        paginationPerPage={perPage}
                        paginationRowsPerPageOptions={[10, 20, 30, 40, 50]}
                        paginationComponentOptions={{
                            rowsPerPageText: "Entries per page:",
                            rangeSeparatorText: "of",
                            selectAllRowsItem: false,
                            selectAllRowsItemText: "All",
                        }}
                        onChangeRowsPerPage={setPerPage}
                        paginationDefaultPage={1}
                    />
                )}
            </div>
                </>
            )}
        </section>
    );
}
